import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
export function Modal({
  isOpen,
  onClose,
  title,
  children
}: ModalProps) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence data-id="element-184">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" aria-hidden="true" data-id="element-185" />

          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none" data-id="element-186">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          duration: 0.5,
          bounce: 0.3
        }} className="w-full max-w-2xl max-h-[85vh] bg-cream rounded-2xl shadow-2xl pointer-events-auto flex flex-col overflow-hidden border border-terracotta/10" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-187">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-terracotta/10 bg-white/50" data-id="element-188">
                <h2 id="modal-title" className="text-xl font-serif font-bold text-warm-dark" data-id="element-189">
                  {title}
                </h2>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-terracotta/10 text-warm-dark/60 hover:text-terracotta transition-colors focus:outline-none focus:ring-2 focus:ring-terracotta" aria-label="Cerrar modal" data-id="element-190">
                  <X className="w-5 h-5" data-id="element-191" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto custom-scrollbar" data-id="element-192">
                <div className="prose prose-stone prose-headings:font-serif prose-headings:text-warm-dark prose-p:text-warm-dark/80 max-w-none" data-id="element-193">
                  {children}
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>, document.body);
}